import { useState } from "react";
import { analyzeMenu, buildQuickOrder } from "../lib/gemini";
import { useAuth } from "../lib/AuthContext";
import { useUserStats } from "../lib/UserStatsContext";
import { useBadgeToast } from "./BadgeToast";
import { useImageUpload } from "../lib/useImageUpload";
import { AIContent } from "./GIBadge";
import { getUserStats, recordMenuDecode, checkAndAwardBadges, checkUsageLimit, recordFeatureUsage } from "../lib/userStats";
import { trackMenuDecodeStart, trackMenuDecoded, trackError, trackAIResponseTime } from "../lib/analytics";
import UsageLimitModal from "./UsageLimitModal";
import UsageIndicator from "./UsageIndicator";
import { BackIcon } from "./Icons";

type Mode = "photo" | "quick-order";

const POPULAR_CHAINS = [
  { id: "chipotle", name: "Chipotle" },
  { id: "subway", name: "Subway" },
  { id: "starbucks", name: "Starbucks" },
  { id: "mcdonalds", name: "McDonald's" },
  { id: "chick-fil-a", name: "Chick-fil-A" },
  { id: "panera", name: "Panera" },
  { id: "other", name: "Other..." },
];

interface MenuDecoderProps {
  onBack?: () => void;
}

function countBadgeLevels(text: string) {
  const giBadges = text.match(/\{\{gi:\d+:gl:(\d+):(\w+)\}\}/g) || [];
  let lowCount = 0, mediumCount = 0, highCount = 0;
  giBadges.forEach((badge) => {
    const levelMatch = badge.match(/:(\w+)\}\}$/);
    if (levelMatch) {
      const level = levelMatch[1];
      if (level === "low") lowCount++;
      else if (level === "medium") mediumCount++;
      else if (level === "high") highCount++;
    }
  });
  return { itemCount: giBadges.length, lowGLCount: lowCount, mediumGLCount: mediumCount, highGLCount: highCount };
}

export default function MenuDecoder({ onBack }: MenuDecoderProps) {
  const { user } = useAuth();
  const { dietaryPrefs } = useUserStats();
  const { showBadge } = useBadgeToast();
  const { preview: menuImage, imageData, fileInputRef, handleImageSelect, removeImage } = useImageUpload();
  const [mode, setMode] = useState<Mode>("photo");
  const [analysis, setAnalysis] = useState<string | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  // Quick Order state
  const [selectedChain, setSelectedChain] = useState<string | null>(null);
  const [customChain, setCustomChain] = useState("");
  const [orderRequest, setOrderRequest] = useState("");
  const [budget, setBudget] = useState("");
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [limitInfo, setLimitInfo] = useState<{ currentUsage: number; isPremiumOnly: boolean }>({ currentUsage: 0, isPremiumOnly: false });
  const [usageRefreshKey, setUsageRefreshKey] = useState(0);

  const recordSuccess = async (result: string, trackingType: string, responseTime: number) => {
    trackAIResponseTime(trackingType, responseTime);
    trackMenuDecoded(countBadgeLevels(result));

    if (user) {
      await recordFeatureUsage(user.uid, "menuDecoder");
      await recordMenuDecode(user.uid);
      setUsageRefreshKey((k) => k + 1);
      const stats = await getUserStats(user.uid);
      const newBadges = await checkAndAwardBadges(user.uid, stats);
      newBadges.forEach((badge) => showBadge(badge));
    }
  };

  const handleAnalyze = async () => {
    if (!imageData || !user) return;

    const usageCheck = await checkUsageLimit(user.uid, "menuDecoder");
    if (!usageCheck.allowed) {
      setLimitInfo({ currentUsage: usageCheck.currentUsage, isPremiumOnly: usageCheck.isPremiumOnly });
      setShowLimitModal(true);
      return;
    }

    setIsAnalyzing(true);
    trackMenuDecodeStart("photo");
    const startTime = Date.now();

    try {
      const result = await analyzeMenu(imageData, dietaryPrefs);
      setAnalysis(result);
      await recordSuccess(result, "menu_decode_photo", Date.now() - startTime);
    } catch (error) {
      console.error("Error analyzing menu:", error);
      trackError({
        errorType: "menu_decode_error",
        errorMessage: error instanceof Error ? error.message : "Unknown error",
        context: "menu_photo_analyze",
      });
      setAnalysis("Sorry, I had trouble reading that menu. Please try a clearer photo.");
    } finally {
      setIsAnalyzing(false);
    }
  };

  const reset = () => {
    removeImage();
    setAnalysis(null);
    setSelectedChain(null);
    setCustomChain("");
    setOrderRequest("");
    setBudget("");
  };

  const handleQuickOrder = async () => {
    const chainName = selectedChain === "other" ? customChain : POPULAR_CHAINS.find(c => c.id === selectedChain)?.name || "";
    if (!chainName || !orderRequest || !user) return;

    const usageCheck = await checkUsageLimit(user.uid, "menuDecoder");
    if (!usageCheck.allowed) {
      setLimitInfo({ currentUsage: usageCheck.currentUsage, isPremiumOnly: usageCheck.isPremiumOnly });
      setShowLimitModal(true);
      return;
    }

    setIsAnalyzing(true);
    trackMenuDecodeStart("text");
    const startTime = Date.now();

    try {
      const result = await buildQuickOrder(chainName, orderRequest, budget || undefined, dietaryPrefs);
      setAnalysis(result);
      await recordSuccess(result, "menu_quick_order", Date.now() - startTime);
    } catch (error) {
      console.error("Error building order:", error);
      trackError({
        errorType: "quick_order_error",
        errorMessage: error instanceof Error ? error.message : "Unknown error",
        context: "menu_quick_order",
      });
      setAnalysis("Sorry, I had trouble building that order. Please try again.");
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-4">
      {onBack && (
        <button onClick={onBack} className="flex items-center gap-1 text-sm text-muted font-[family-name:--font-ui] mb-4 hover:text-text">
          <BackIcon className="w-4 h-4" /> Back
        </button>
      )}
      <div className="section-label">Menu Decoder</div>
      <p className="text-sm text-muted mb-6">
        {mode === "photo" ? "Snap a menu photo and get the best low-GI choices highlighted" : "Build a custom low-GI order from your favorite chains"}
      </p>

      {/* Mode Toggle */}
      {!analysis && (
        <div className="flex gap-4 mb-6 border-b border-border">
          {(["photo", "quick-order"] as Mode[]).map((m) => (
            <button
              key={m}
              onClick={() => { setMode(m); reset(); }}
              className={`pb-2 text-sm font-[family-name:--font-ui] font-medium transition-colors ${
                mode === m
                  ? "text-accent border-b-2 border-accent"
                  : "text-muted hover:text-text"
              }`}
            >
              {m === "photo" ? "Photo" : "Quick Order"}
            </button>
          ))}
        </div>
      )}

      {/* Quick Order Mode */}
      {mode === "quick-order" && !analysis && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-muted font-[family-name:--font-ui] font-medium mb-2">Where are you eating?</label>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {POPULAR_CHAINS.map((chain) => (
                <button
                  key={chain.id}
                  onClick={() => setSelectedChain(chain.id)}
                  className={`p-3 rounded-md border text-center transition-colors ${
                    selectedChain === chain.id
                      ? "border-accent bg-accent/5 text-accent"
                      : "border-border hover:border-accent/50"
                  }`}
                >
                  <div className="text-sm font-medium truncate">{chain.name}</div>
                </button>
              ))}
            </div>
          </div>

          {selectedChain === "other" && (
            <input
              type="text"
              value={customChain}
              onChange={(e) => setCustomChain(e.target.value)}
              placeholder="Enter restaurant name..."
              className="w-full px-4 py-3 border border-border rounded-md bg-bg text-text focus:border-accent outline-none"
            />
          )}

          <div>
            <label className="block text-sm text-muted font-[family-name:--font-ui] font-medium mb-2">What are you looking for?</label>
            <textarea
              value={orderRequest}
              onChange={(e) => setOrderRequest(e.target.value)}
              placeholder="e.g., Low-carb lunch that's filling, vegetarian option with lots of protein, quick breakfast under 300 calories..."
              className="w-full px-4 py-3 border border-border rounded-md bg-bg text-text focus:border-accent outline-none resize-none"
              rows={3}
            />
          </div>

          <div>
            <label className="block text-sm text-muted font-[family-name:--font-ui] font-medium mb-2">Budget (optional)</label>
            <input
              type="text"
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
              placeholder="e.g., under $15"
              className="w-full px-4 py-3 border border-border rounded-md bg-bg text-text focus:border-accent outline-none"
            />
          </div>

          <button
            onClick={handleQuickOrder}
            disabled={isAnalyzing || !selectedChain || !orderRequest || (selectedChain === "other" && !customChain)}
            className="w-full px-4 py-3 bg-accent text-white rounded-md font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isAnalyzing ? (
              <>
                <div className="spinner spinner--sm"></div>
                Building your order...
              </>
            ) : (
              "Build My Order"
            )}
          </button>

          <div className="pt-4 border-t border-border">
            <p className="text-sm text-muted font-[family-name:--font-ui] font-medium mb-2">Quick Order tips</p>
            <ul className="text-sm text-muted space-y-1">
              <li>&mdash; Be specific about your goals (low-carb, high-protein, etc.)</li>
              <li>&mdash; Mention dietary restrictions in your request</li>
              <li>&mdash; A complete order with customizations will be built for you</li>
            </ul>
          </div>
        </div>
      )}

      {/* Photo Mode */}
      {mode === "photo" && !menuImage && (
        <div className="space-y-4">
          <div
            onClick={() => fileInputRef.current?.click()}
            className="border border-dashed border-border rounded-md p-8 text-center cursor-pointer hover:border-accent transition-colors"
          >
            <p className="text-muted font-medium">Upload a menu photo</p>
            <p className="text-muted text-sm mt-1">Works best with clear, well-lit photos</p>
          </div>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImageSelect}
            accept="image/*"
            capture="environment"
            className="hidden"
          />

          <div className="pt-4 border-t border-border">
            <p className="text-sm text-muted font-[family-name:--font-ui] font-medium mb-2">Tips for best results</p>
            <ul className="text-sm text-muted space-y-1">
              <li>&mdash; Take a photo of one page at a time</li>
              <li>&mdash; Ensure text is clearly visible</li>
              <li>&mdash; Include item descriptions if available</li>
            </ul>
          </div>
        </div>
      )}

      {/* Photo Mode - With Image */}
      {mode === "photo" && menuImage && !analysis && (
        <div className="space-y-4">
          <div className="relative">
            <img
              src={menuImage}
              alt="Menu"
              className="w-full rounded-md border border-border"
            />
            <button
              onClick={reset}
              className="absolute top-2 right-2 w-8 h-8 bg-bg/90 rounded-md flex items-center justify-center text-muted hover:text-text border border-border"
            >
              &times;
            </button>
          </div>

          <button
            onClick={handleAnalyze}
            disabled={isAnalyzing}
            className="w-full px-4 py-3 bg-accent text-white rounded-md font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isAnalyzing ? (
              <>
                <div className="spinner spinner--sm"></div>
                Reading menu...
              </>
            ) : (
              "Analyze Menu"
            )}
          </button>
        </div>
      )}

      {/* Analysis Results (both modes) */}
      {analysis && (
        <div className="space-y-4">
          {menuImage && (
            <div className="relative">
              <img
                src={menuImage}
                alt="Menu"
                className="w-full rounded-md border border-border"
              />
            </div>
          )}

          <div className="border-t border-border pt-4">
            <AIContent content={analysis} />
          </div>

          <button
            onClick={reset}
            className="w-full px-4 py-3 border border-border text-text rounded-md hover:bg-black/5 transition-colors"
          >
            {mode === "photo" ? "Analyze Another Menu" : "Build Another Order"}
          </button>
        </div>
      )}

      {/* Usage Indicator */}
      <div className="mt-4 flex justify-center">
        <UsageIndicator feature="menuDecoder" compact onLimitReached={() => setShowLimitModal(true)} refreshKey={usageRefreshKey} />
      </div>

      {/* Usage Limit Modal */}
      <UsageLimitModal
        isOpen={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        feature="menuDecoder"
        currentUsage={limitInfo.currentUsage}
        isPremiumOnly={limitInfo.isPremiumOnly}
      />
    </div>
  );
}
